import { differenceInMinutes, endOfWeek, isWithinInterval, startOfWeek } from "date-fns";

export const PAYMENT_STATUS = {
  PAID: "PAID",
  UNPAID: "UNPAID",
};

export const PAYMENT_METHOD = {
  CASH: "CASH",
  BANK: "BANK",
};

export const paymentMethodLabel = (method) => {
  if (method === PAYMENT_METHOD.CASH) return "Cash";
  if (method === PAYMENT_METHOD.BANK) return "Bank";
  return method || "—";
};

export const formatDuration = (minutes) => {
  const total = Math.max(0, Math.round(Number(minutes) || 0));
  const hours = Math.floor(total / 60);
  const mins = total % 60;
  if (!hours && !mins) return "0h 00m";
  return `${hours}h ${String(mins).padStart(2, "0")}m`;
};

export const unwrapList = (payload) => {
  if (Array.isArray(payload)) return payload;
  if (Array.isArray(payload?.data)) return payload.data;
  if (Array.isArray(payload?.content)) return payload.content;
  if (Array.isArray(payload?.data?.content)) return payload.data.content;
  if (Array.isArray(payload?.data?.data)) return payload.data.data;
  return [];
};

export const unwrapItem = (payload) => {
  if (!payload || Array.isArray(payload)) return payload ?? null;
  const nested = payload?.data;
  if (nested && typeof nested === "object" && !Array.isArray(nested) && !nested.content) {
    return nested;
  }
  return nested ?? payload;
};

export const unwrapPage = (payload) => {
  const nested = payload?.data && !Array.isArray(payload?.data) ? payload.data : payload;
  const content = unwrapList(nested);
  return {
    content,
    totalItems: Number(nested?.totalItems ?? nested?.totalElements ?? content.length) || 0,
    totalPages: Number(nested?.totalPages) || 1,
    summary: nested?.summary || payload?.summary || null,
  };
};

export const normalizePaymentStatus = (value) => {
  if (value === true || value === "PAID" || value === "paid" || value === "Paid") {
    return PAYMENT_STATUS.PAID;
  }
  return PAYMENT_STATUS.UNPAID;
};

export const minutesBetween = (start, end) => {
  if (!start) return 0;
  const from = new Date(start);
  const to = end ? new Date(end) : new Date();
  if (Number.isNaN(from.getTime()) || Number.isNaN(to.getTime())) return 0;
  return Math.max(0, differenceInMinutes(to, from));
};

export const entryBreakMinutes = (entry) =>
  Math.max(0, Math.round(Number(entry?.breakMinutes ?? entry?.breakDurationMinutes ?? 0) || 0));

export const entryGrossMinutes = (entry) => {
  const recorded = Number(entry?.durationMinutes ?? entry?.totalMinutes ?? entry?.elapsedMinutes);
  if (Number.isFinite(recorded) && recorded > 0) return recorded;
  return minutesBetween(entry?.clockIn ?? entry?.clockInAt, entry?.clockOut ?? entry?.clockOutAt);
};

export const entryDurationMinutes = (entry) =>
  Math.max(0, entryGrossMinutes(entry) - entryBreakMinutes(entry));

export const formatHoursDecimal = (minutes) => {
  const hours = (Number(minutes) || 0) / 60;
  return hours.toFixed(2);
};

export const isOpenEntry = (entry) =>
  Boolean((entry?.clockIn || entry?.clockInAt) && !(entry?.clockOut || entry?.clockOutAt));

export const currentWeekInterval = (date = new Date()) => ({
  start: startOfWeek(date, { weekStartsOn: 1 }),
  end: endOfWeek(date, { weekStartsOn: 1 }),
});

export const isInCurrentWeek = (value, date = new Date()) => {
  if (!value) return false;
  const parsed = new Date(value);
  if (Number.isNaN(parsed.getTime())) return false;
  return isWithinInterval(parsed, currentWeekInterval(date));
};

export const employeeClockInUrl = () => `${window.location.origin}/employee/clock-in`;
